// src/store/api/regionApi.ts
import {baseApi} from "./baseApi";
import type {
  CreateRegionDto,
  UpdateRegionDto,
  RegionFilterDto,
} from "../../types/region";

interface RegionResponseDto {
  id: number;
  nameEng: string;
  nameMm: string;
  slug: string;
  createdAt: Date;
  updatedAt: Date;
  townshipCount?: number;
  propertyCount?: number;
}

interface RegionStatsDto {
  totalRegions: number;
  mostPopularRegion: string;
  mostPopularRegionCount: number;
  averagePropertiesPerRegion: number;
  regionsWithoutProperties: number;
}

interface PaginatedRegionResponseDto {
  data: RegionResponseDto[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  stats?: RegionStatsDto;
}

export const regionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Region
    createRegion: builder.mutation<RegionResponseDto, CreateRegionDto>({
      query: (data) => ({
        url: "/regions",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Region"],
    }),

    // Get Regions with filters and pagination
    getRegions: builder.query<PaginatedRegionResponseDto, RegionFilterDto>({
      query: (params) => ({
        url: "/regions",
        params,
      }),
      transformResponse: (response: {data: PaginatedRegionResponseDto}) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({id}) => ({type: "Region" as const, id})),
              {type: "Region", id: "LIST"},
            ]
          : [{type: "Region", id: "LIST"}],
    }),

    // Get Regions for Dropdown (simplified)
    getRegionsDropdown: builder.query<
      Array<{id: number; nameEng: string; nameMm: string; slug: string}>,
      void
    >({
      query: () => "/regions/dropdown",
      transformResponse: (response: {data: Array<{id: number; nameEng: string; nameMm: string; slug: string}>}) => response.data,
      providesTags: ["Region"],
    }),

    // Get Region Stats
    getRegionStats: builder.query<RegionStatsDto, void>({
      query: () => "/regions/stats",
      transformResponse: (response: {data: RegionStatsDto}) => response.data,
      providesTags: ["Region"],
    }),

    // Get Region by ID
    getRegionById: builder.query<RegionResponseDto, number>({
      query: (id) => `/regions/${id}`,
      transformResponse: (response: {data: RegionResponseDto}) => response.data,
      providesTags: (result, error, id) => [{type: "Region", id}],
    }),

    // Get Region by Slug
    getRegionBySlug: builder.query<RegionResponseDto, string>({
      query: (slug) => `/regions/slug/${slug}`,
      transformResponse: (response: {data: RegionResponseDto}) => response.data,
      providesTags: (result) =>
        result ? [{type: "Region", id: result.id}] : [],
    }),

    // Update Region
    updateRegion: builder.mutation<
      RegionResponseDto,
      {id: number; data: UpdateRegionDto}
    >({
      query: ({id, data}) => ({
        url: `/regions/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Region", id},
        {type: "Region", id: "LIST"},
      ],
    }),

    // Delete Region
    deleteRegion: builder.mutation<{success: boolean; message: string}, number>(
      {
        query: (id) => ({
          url: `/regions/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: (result, error, id) => [
          {type: "Region", id},
          {type: "Region", id: "LIST"},
        ],
      }
    ),

    // Bulk Create Regions
    bulkCreateRegions: builder.mutation<
      {success: boolean; message: string; createdCount: number},
      {regions: CreateRegionDto[]}
    >({
      query: (data) => ({
        url: "/regions/bulk",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Region"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateRegionMutation,
  useGetRegionsQuery,
  useGetRegionsDropdownQuery,
  useGetRegionStatsQuery,
  useGetRegionByIdQuery,
  useGetRegionBySlugQuery,
  useUpdateRegionMutation,
  useDeleteRegionMutation,
  useBulkCreateRegionsMutation,
} = regionApi;
